<template>
  <div class="delivery-estimator">
    <p>
      Enter your postcode below to see the estimated delivery date for your
      order.
    </p>

    <form
      class="form delivery-estimator__form"
      @submit.prevent="handleFormSubmit"
    >
      <label class="field field--combo">
        <div class="input">
          <input
            v-model.trim="postcode"
            type="text"
            class="input__element"
            placeholder="Enter postcode"
          />
        </div>
        <div class="input input--submit">
          <Button type="submit" transparent>SEE DELIVERY DATE</Button>
        </div>
      </label>
      <div v-if="v$.postcode.$dirty" class="input__response">
        <ul>
          <li v-for="error of v$.postcode.$errors" :key="error.$uid">
            {{ error.$message }}
          </li>
        </ul>
      </div>
      <div v-if="dateError" class="input__response">
        <p>Sorry, we can't get you a date right now</p>
      </div>
    </form>

    <template v-if="estimatedDeliveryDate">
      <p class="delivery-estimator__results">
        {{ deliveryService }} - {{ estimatedDeliveryDate }}
      </p>
      <p v-if="showRM24Saturday" class="delivery-estimator__results">
        Saturday Next Day Delivery - {{ rm24SaturdayDate }}
      </p>
    </template>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Button from "@/components/Button.vue";
import format from "date-fns/format";
import addDays from "date-fns/addDays";
import getDay from "date-fns/getDay";
import isFriday from "date-fns/isFriday";
import isBefore from "date-fns/isBefore";
import { mapState, mapActions, mapGetters } from "vuex";
import { checkDeliveryDates } from "@/helpers/edd.js";
import * as Sentry from "@sentry/browser";

export default {
  name: "DeliveryEstimator",
  components: {
    Button,
  },
  props: {
    weight: {
      type: Number,
      default: 0.2,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      deliveryService: null,
      estimatedDeliveryDate: null,
      dateError: false,
    };
  },
  computed: {
    ...mapState("app", ["userCountry", "deliveryEstimate"]),
    ...mapGetters("app", ["userPostCode"]),
    postcode: {
      get() {
        return this.userPostCode;
      },
      set(value) {
        this.setUserPreferences({
          key: "userPostCode",
          value,
        });
      },
    },
    showRM24Saturday() {
      if (this.userCountry === "GB") {
        const currentDate = new Date();
        if (isFriday(currentDate)) {
          if (isBefore(currentDate, new Date().setHours(14, 0, 0, 0))) {
            return true;
          }
        }
      }

      return false;
    },
    rm24SaturdayDate() {
      const currentDate = new Date();
      const currentDayOfWeek = getDay(currentDate);
      const daysUntilSaturday = (6 - currentDayOfWeek + 7) % 7;
      const upcomingSaturday = addDays(currentDate, daysUntilSaturday);

      return format(new Date(upcomingSaturday), "eeee do MMMM yyyy");
    },
  },
  watch: {
    userCountry(newVal, oldVal) {
      console.log(newVal, oldVal);
      if (oldVal === null && newVal !== null) {
        if (this.postcode) {
          this.handleFormSubmit();
        }
      }
    },
  },
  methods: {
    ...mapActions("app", ["setUserPreferences", "getDeliveryEstimate"]),
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      await this.getDeliveryEstimate({
        weight: this.weight,
      });

      const { estimated_delivery_date, service_type_name } =
        this.deliveryEstimate;

      if (!estimated_delivery_date) {
        this.dateError = true;
        return;
      }

      const datesAreValid = checkDeliveryDates([estimated_delivery_date]);

      if (!datesAreValid) {
        this.dateError = true;

        Sentry.addBreadcrumb({
          category: "delivery-estimate",
          message: "Estimated dates",
          data: { estimated_delivery_date },
          level: "info",
        });
        Sentry.captureMessage("EDD dates don't pass validation");
        return;
      }

      this.estimatedDeliveryDate = format(
        new Date(estimated_delivery_date),
        "eeee do MMMM yyyy"
      );

      this.deliveryService = service_type_name;
    },
  },
  validations() {
    return {
      postcode: { required },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.delivery-estimator {
  padding: 15px 20px;
  background-color: #fafafa;
  margin-bottom: 16px;

  p {
    font-size: 11px;
  }

  &__form {
    margin: 6px 0 0;
  }

  &__results {
    margin-top: 10px;
  }
}
</style>
